import * as api from "../../services/packages-service";
import { createAsyncThunk } from "@reduxjs/toolkit";

// ── Quick Trips ──

export const fetchAllQuickTrips = createAsyncThunk(
  "packages/fetchAllQuickTrips",
  async (params, { rejectWithValue }) => {
    try {
      return await api.getAllQuickTrips(params);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchQuickTripById = createAsyncThunk(
  "packages/fetchQuickTripById",
  async (id, { rejectWithValue }) => {
    try {
      return await api.getQuickTripById(id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const createQuickTrip = createAsyncThunk(
  "packages/createQuickTrip",
  async (data, { rejectWithValue }) => {
    try {
      return await api.createQuickTrip(data);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const updateQuickTrip = createAsyncThunk(
  "packages/updateQuickTrip",
  async (payload, { rejectWithValue }) => {
    // payload: { id, data }
    try {
      return await api.updateQuickTrip(payload);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const deleteQuickTrip = createAsyncThunk(
  "packages/deleteQuickTrip",
  async (id, { rejectWithValue }) => {
    try {
      return await api.deleteQuickTrip(id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const restoreQuickTrip = createAsyncThunk(
  "packages/restoreQuickTrip",
  async (id, { rejectWithValue }) => {
    try {
      return await api.restoreQuickTrip(id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const uploadQuickTripImages = createAsyncThunk(
  "packages/uploadQuickTripImages",
  async (payload, { rejectWithValue }) => {
    // payload: { id, files }
    try {
      return await api.uploadQuickTripImages(payload);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// ── Packages ──

export const fetchAllPackages = createAsyncThunk(
  "packages/fetchAllPackages",
  async (params, { rejectWithValue }) => {
    try {
      return await api.getAllPackages(params);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchPackageById = createAsyncThunk(
  "packages/fetchPackageById",
  async (id, { rejectWithValue }) => {
    try {
      return await api.getPackageById(id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const createPackage = createAsyncThunk(
  "packages/createPackage",
  async (data, { rejectWithValue }) => {
    try {
      return await api.createPackage(data);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const updatePackage = createAsyncThunk(
  "packages/updatePackage",
  async (payload, { rejectWithValue }) => {
    // payload: { id, data } — data may be a FormData (image upload) or plain object
    try {
      return await api.updatePackage(payload);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const deletePackage = createAsyncThunk(
  "packages/deletePackage",
  async (id, { rejectWithValue }) => {
    try {
      return await api.deletePackage(id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const restorePackage = createAsyncThunk(
  "packages/restorePackage",
  async (id, { rejectWithValue }) => {
    try {
      return await api.restorePackage(id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const uploadPackageImages = createAsyncThunk(
  "packages/uploadPackageImages",
  async (payload, { rejectWithValue }) => {
    // payload: { id, files }
    try {
      return await api.uploadPackageImages(payload);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// ── Customizations ──

export const fetchCustomizationOptions = createAsyncThunk(
  "packages/fetchCustomizationOptions",
  async (params, { rejectWithValue }) => {
    try {
      return await api.getCustomizationOptions(params);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const createCustomizationOption = createAsyncThunk(
  "packages/createCustomizationOption",
  async (data, { rejectWithValue }) => {
    try {
      return await api.createCustomizationOption(data);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const updateCustomizationOption = createAsyncThunk(
  "packages/updateCustomizationOption",
  async (payload, { rejectWithValue }) => {
    // payload: { id, data }
    try {
      return await api.updateCustomizationOption(payload);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const deleteCustomizationOption = createAsyncThunk(
  "packages/deleteCustomizationOption",
  async (id, { rejectWithValue }) => {
    try {
      await api.deleteCustomizationOption(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchPackageCustomizations = createAsyncThunk(
  "packages/fetchPackageCustomizations",
  async (packageId, { rejectWithValue }) => {
    try {
      const data = await api.getPackageCustomizations(packageId);
      return { packageId, data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const addPackageCustomization = createAsyncThunk(
  "packages/addPackageCustomization",
  async (payload, { rejectWithValue }) => {
    // payload: { packageId, data }
    try {
      return await api.addPackageCustomization(payload);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const removePackageCustomization = createAsyncThunk(
  "packages/removePackageCustomization",
  async (payload, { rejectWithValue }) => {
    // payload: { packageId, optionId }
    try {
      const response = await api.removePackageCustomization(payload);
      return { ...payload, ...response };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// ── Saved data (read-only) ──

export const fetchAllSavedData = createAsyncThunk(
  "packages/fetchAllSavedData",
  async (params, { rejectWithValue }) => {
    try {
      return await api.getAllSavedData(params);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// ── Bookings (read-only) ──

export const fetchAllPackageBookings = createAsyncThunk(
  "packages/fetchAllPackageBookings",
  async (params, { rejectWithValue }) => {
    try {
      return await api.getAllPackageBookings(params);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchPackageBookingById = createAsyncThunk(
  "packages/fetchPackageBookingById",
  async (id, { rejectWithValue }) => {
    try {
      return await api.getPackageBookingById(id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// ── Custom package requests ──

export const fetchAllCustomPackageRequests = createAsyncThunk(
  "packages/fetchAllCustomPackageRequests",
  async (params, { rejectWithValue }) => {
    try {
      return await api.getAllCustomPackageRequests(params);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchCustomPackageRequestById = createAsyncThunk(
  "packages/fetchCustomPackageRequestById",
  async (id, { rejectWithValue }) => {
    try {
      return await api.getCustomPackageRequestById(id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const updateCustomPackageRequestStatus = createAsyncThunk(
  "packages/updateCustomPackageRequestStatus",
  async (payload, { rejectWithValue }) => {
    // payload: { id, status }
    try {
      return await api.updateCustomPackageRequestStatus(payload);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const deleteCustomPackageRequest = createAsyncThunk(
  "packages/deleteCustomPackageRequest",
  async (id, { rejectWithValue }) => {
    try {
      await api.deleteCustomPackageRequest(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);
